//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';

import {
  DropdownSection,
  DropdownSectionType,
  DropdownValue,
} from '@components/molecules/smart-input/smartInputModel';

import { makeStateFactoryQueryParams } from 'app/initialization/routing/stateHelpers';

export const LIST_VIEW_TOP_ROW_HEIGHT = theme.spacings[7];
export const LIST_VIEW_INTENT_FILTER_HEIGHT = theme.spacings[6];

export const MAX_MESSAGE_LENGTH = 2200;
export const TIKTOK_MAX_MESSAGE_LENGTH = 150;
export const NUM_OF_ROWS = 3;

export const SOCIAL_INBOX_MANAGE_PERMISSION = 'social_inbox:manage';
export const SOCIAL_INBOX_PERMISSION_ERROR_MESSAGE =
  "You don't have permission to complete this action. Contact a group admin if this is a mistake.";
export const SOCIAL_INBOX_MARK_READ_PERMISSION_MESSAGE =
  'Elevated permissions are required to mark messages as read. Please elevate your access to perform this action.';

export const DETAILED_VIEW_WRAPPER_ID = 'detailed-view-wrapper';
export const POST_AND_RESPONSE_SECTION_HEIGHT = 350;

export const ACCOUNTS_NOT_CONNECTED =
  'Accounts Not Connected. Please connect your Instagram, Facebook, or TikTok account to continue.';
export const COMMENT_NOT_FOUND_MESSAGE =
  'This comment was deleted on Instagram and is no longer available.';
export const EMPTY_MESSAGE_ALERT =
  'No comments available. The last comment has been deleted. Please adjust your search or filter settings to see more comments.';
export const MEDIA_EXPIRED = 'Media expired';
export const MISSING_FACEBOOK_PAGE_TOKEN_ERROR_MESSAGE =
  'Your Facebook connection is no longer valid. Refresh your connection to continue using Facebook and Instagram features.';
export const UNKNOWN_ERROR_MESSAGE =
  'An unknown error occurred when attempting to fetch details';
export const SEARCH_DEBOUNCE_DELAY = 300;
export const AVATAR_SIZE = 32;
export const ELLIPSIZE_CHARACTER_LIMIT = 3;
export const RESPONDED_BADGE_TEXT = 'Responded';
export const REFRESH_CONNECTION_MESSAGE =
  'We’re missing required permissions to access your Instagram DMs. Please re-connect your account and ensure messaging permissions are granted on both Facebook and Instagram.';
export const MESSAGE_ACCESS_STATUS =
  'You’re almost there! We need one final step to enable DMs. Please enable message access in your Instagram app: Settings > Messages and Story Replies > Message controls > Allow Access to Messages. Once done, click refresh button.';
export const INSTAGRAM_ACCOUNT_NOT_CONNECTED_MESSAGE =
  'Instagram account not connected. Please connect your account to continue receiving Instagram updates in Inbox.';
export const FACEBOOK_PAGE_NOT_CONNECTED_MESSAGE =
  'Your Facebook page connection is missing or invalid. You may not be able to see Instagram messages. Please refresh your connection to restore full functionality.';
export const SOCIAL_NETWORK_ICON_SIZE = 16;
export const GENERATE_AI_RESPONSE_DEBOUNCE_DELAY = 500;
export const UNSUPPORTED_MEDIA = 'Unsupported media';
export const BANNER_HEIGHT = 40;
export const BANNER_HEIGHT_WHEN_AI_ENABLED = 110;
export const MESSAGES_INITIALLY_VISIBLE = 1;
export const MAX_REPLY_DEPTH = 2;
export const MODAL_MEDIA_EMBED_HEIGHT = '600';
export const INBOX_SETUP_DOCS_URL =
  'https://docs.bazaarvoice.com/articles/?readerUiPreview=1#!vibe/inbox';

/**
 * URL query param keys for Social Inbox deep-linking.
 * Follows the Content Library pattern (see mediaLibraryConstants.ts).
 */
export const socialInboxQueryParams = [
  'intents',
  'startTime',
  'endTime',
  'isRead',
  'isResponded',
  'isUrgent',
  'instagram',
  'facebook',
  'tiktok',
  'comment',
  'mention',
] as const;

export const socialInboxQueryKeys: string = makeStateFactoryQueryParams([
  ...socialInboxQueryParams,
]);

export enum SOCIAL_RESPONSE_METRICS_ICONS {
  LIKES = 'likes',
  COMMENTS = 'comments',
}

export interface ISocialResponseFallbackText {
  title: string;
  subtitle: string;
}

export const NO_DATA_FOUND_FALLBACK_TEXT: ISocialResponseFallbackText = {
  title: 'No messages found.',
  subtitle: '',
};

export const DEFAULT_SMART_INPUT_KEY = 'default-smart-input';
export const RESET_DEFAULT_SMART_INPUT_KEY = 'reset-default-smart-input';
export const SMART_INPUT_WITH_USER_HANDLE_KEY = 'smart-input-user-handle';
export const SMART_INPUT_WITH_AI_GENERATED_RESPONSE =
  'smart-input-ai-generated-response';
const SMART_INPUT_AI_MENU_DISABLED_TOOLTIP =
  'You must enter some text for our AI to improve a response.';

export const typeToLabelKeyMap: Record<string, string> = {
  COMMENT: 'Comment',
  MENTION: 'Comment',
  POST: 'Comment',
  DM: 'Message',
  DEFAULT: 'Item',
  // Add more type-to-key mappings as needed in the future
};

export const socialInboxGenerativeAiDropdown: DropdownSection = {
  label: 'Social Inbox Generative AI Options',
  type: DropdownSectionType.STANDARD,
  options: [
    {
      canBeDisabled: false,
      label: 'Generate new reply',
      value: DropdownValue.GENERATE_REPLY,
    },
    {
      canBeDisabled: true,
      label: 'Improve writing',
      tooltip: SMART_INPUT_AI_MENU_DISABLED_TOOLTIP,
      value: DropdownValue.IMPROVE_WRITING,
    },
    {
      canBeDisabled: true,
      label: 'Fix spelling and grammar',
      tooltip: SMART_INPUT_AI_MENU_DISABLED_TOOLTIP,
      value: DropdownValue.FIX_SPELLING_AND_GRAMMAR,
    },
    {
      canBeDisabled: true,
      label: 'Make shorter',
      tooltip: SMART_INPUT_AI_MENU_DISABLED_TOOLTIP,
      value: DropdownValue.MAKE_SHORTER,
    },
    {
      canBeDisabled: true,
      label: 'Make longer',
      tooltip: SMART_INPUT_AI_MENU_DISABLED_TOOLTIP,
      value: DropdownValue.MAKE_LONGER,
    },
    {
      canBeDisabled: true,
      label: 'Simplify language',
      tooltip: SMART_INPUT_AI_MENU_DISABLED_TOOLTIP,
      value: DropdownValue.SIMPLIFY_LANGUAGE,
    },
    {
      canBeDisabled: true,
      label: 'Change tone',
      tooltip: SMART_INPUT_AI_MENU_DISABLED_TOOLTIP,
      value: DropdownValue.CHANGE_TONE,
      subOptions: [
        {
          canBeDisabled: true,
          label: 'Professional',
          value: DropdownValue.TONE_PROFESSIONAL,
        },
        {
          canBeDisabled: true,
          label: 'Casual',
          value: DropdownValue.TONE_CASUAL,
        },
        {
          canBeDisabled: true,
          label: 'Straightforward',
          value: DropdownValue.TONE_STRAIGHTFORWARD,
        },
        {
          canBeDisabled: true,
          label: 'Confident',
          value: DropdownValue.TONE_CONFIDENT,
        },
        {
          canBeDisabled: true,
          label: 'Friendly',
          value: DropdownValue.TONE_FRIENDLY,
        },
      ],
    },
  ],
};
